import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  BadgeCheck,
  Landmark,
  Users,
  Wallet,
  LineChart,
  HeartHandshake,
  PiggyBank,
  ScrollText,
  ArrowRight,
  Check,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import NewsletterForm from "@/components/NewsletterForm";

const IMG = {
  hero: "/images/hero.jpg",
  family: "/images/family.jpg",
  advisor: "/images/advisor.jpg",
  documents: "/images/documents.jpg",
  chart: "/images/chart.jpg",
  consult: "/images/consult.jpg",
  couple: "/images/couple.jpg",
  office: "/images/office.jpg",
};

const SERVICE_ICONS = {
  budget: Wallet,
  debt: ScrollText,
  benefits: HeartHandshake,
  retirement: PiggyBank,
  savings: BadgeCheck,
  protection: ShieldCheck,
};

export default function Home() {
  const { locale, t, dict } = useI18n();
  const l = (p) => `/${locale}${p === "/" ? "" : p}`;

  const stats = Object.entries(dict.stats.items);
  const services = Object.entries(dict.services.items);
  const steps = Object.entries(dict.steps.items);
  const eligibility = dict.eligibility.items;
  const testimonials = Object.entries(dict.testimonials.items);
  const trust = Object.entries(dict.trustBar.items);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0b3d5c] text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:grid-cols-2 md:items-center md:gap-16 md:px-8 md:py-24">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-white/85">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#7ec4a1]" />
              {t("hero.eyebrow")}
            </div>
            <h1
              className="mt-6 text-4xl leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              data-testid="hero-title"
            >
              {t("hero.title")}
            </h1>
            <p className="mt-6 max-w-xl text-base text-[#c9d9e5] md:text-lg">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`${l("/")}#newsletter`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0f5c3f] px-6 py-3 text-sm font-semibold hover:bg-[#0d4d35]"
                data-testid="hero-primary-cta"
              >
                {t("hero.primaryCta")} <ArrowRight size={16} />
              </Link>
              <Link
                to={`${l("/")}#steps`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
                data-testid="hero-secondary-cta"
              >
                {t("hero.secondaryCta")}
              </Link>
            </div>
            <p className="mt-6 flex items-center gap-2 text-xs text-white/70">
              <ShieldCheck size={14} /> {t("hero.trust")}
            </p>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img
                src={IMG.hero}
                alt={t("images.heroAlt")}
                loading="eager"
                className="h-full w-full object-cover"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-6 -left-4 hidden w-64 rounded-xl border border-[#dbe4ea] bg-white p-4 text-[#0b3d5c] shadow-xl md:block">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0f5c3f] text-white">
                  <BadgeCheck size={18} />
                </span>
                <p className="text-xs font-semibold uppercase tracking-[0.14em]">
                  {t("footer.licenseLabel")}
                </p>
              </div>
              <p className="mt-2 text-sm font-semibold">{t("footer.license")}</p>
            </div>
          </div>
        </div>

        {/* Stat bar */}
        <div className="border-t border-white/10 bg-[#08324b]">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-5 py-8 md:grid-cols-4 md:gap-8 md:px-8">
            {stats.map(([k, s]) => (
              <div key={k} data-testid={`stat-${k}`}>
                <p className="text-2xl font-semibold text-white md:text-3xl">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-white/60">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREO BLOCKS */}
      <section id="guides" className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <div className="grid gap-16">
            {[
              { key: "support", img: IMG.family, alt: t("images.familyAlt"), reverse: false, icon: HeartHandshake },
              { key: "literacy", img: IMG.chart, alt: t("images.chartAlt"), reverse: true, icon: LineChart },
              { key: "consult", img: IMG.advisor, alt: t("images.advisorAlt"), reverse: false, icon: Users },
            ].map((block) => {
              const Icon = block.icon;
              const b = dict.creoBlocks[block.key];
              return (
                <div
                  key={block.key}
                  className={`grid gap-10 md:grid-cols-2 md:items-center md:gap-16 ${
                    block.reverse ? "md:[&>div:first-child]:order-2" : ""
                  }`}
                  data-testid={`creo-block-${block.key}`}
                >
                  <div className="overflow-hidden rounded-2xl border border-[#dbe4ea]">
                    <img src={block.img} alt={block.alt} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-[#eef4ef] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#0f5c3f]">
                      <Icon size={14} /> {b.eyebrow}
                    </div>
                    <h2
                      className="mt-4 text-3xl leading-tight text-[#0b3d5c] md:text-4xl"
                      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                    >
                      {b.title}
                    </h2>
                    <p className="mt-4 max-w-lg text-base leading-relaxed text-[#4b5f70]">{b.text}</p>
                    <Link
                      to={`${l("/")}#newsletter`}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0f5c3f] hover:text-[#0b3d5c]"
                      data-testid={`creo-cta-${block.key}`}
                    >
                      {t("cta.consult")} <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-[#f2f6f9]">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <div className="max-w-2xl">
            <h2
              className="text-3xl leading-tight text-[#0b3d5c] md:text-4xl"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              data-testid="services-title"
            >
              {t("services.title")}
            </h2>
            <p className="mt-4 text-base text-[#4b5f70]">{t("services.subtitle")}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map(([key, item]) => {
              const Icon = SERVICE_ICONS[key] || Wallet;
              return (
                <div
                  key={key}
                  className="rounded-xl border border-[#dbe4ea] bg-white p-6 shadow-sm"
                  data-testid={`service-card-${key}`}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0b3d5c] text-white">
                    <Icon size={18} />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-[#0b3d5c]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#4b5f70]">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section id="steps" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:grid-cols-[1.1fr_1fr] md:items-center md:gap-16 md:px-8">
          <div className="overflow-hidden rounded-2xl border border-[#dbe4ea]">
            <img src={IMG.consult} alt={t("images.consultAlt")} loading="lazy" className="h-full w-full object-cover" />
          </div>
          <div>
            <h2
              className="text-3xl leading-tight text-[#0b3d5c] md:text-4xl"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              data-testid="steps-title"
            >
              {t("steps.title")}
            </h2>
            <p className="mt-4 text-base text-[#4b5f70]">{t("steps.subtitle")}</p>
            <ol className="mt-8 space-y-6">
              {steps.map(([key, item]) => (
                <li key={key} className="border-l-2 border-[#0f5c3f] pl-5" data-testid={`step-${key}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#0f5c3f]">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#4b5f70]">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section id="about" className="bg-[#0b3d5c] text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:grid-cols-[1fr_1fr] md:items-center md:gap-16 md:px-8">
          <div>
            <h2
              className="text-3xl leading-tight md:text-4xl"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              data-testid="eligibility-title"
            >
              {t("eligibility.title")}
            </h2>
            <p className="mt-4 text-base text-[#c9d9e5]">{t("eligibility.subtitle")}</p>
            <ul className="mt-8 space-y-3">
              {eligibility.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-white/90" data-testid={`eligibility-item-${idx}`}>
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#0f5c3f] text-white">
                    <Check size={12} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-xs leading-relaxed text-white/60">{t("eligibility.disclaimer")}</p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <img src={IMG.documents} alt={t("images.documentsAlt")} loading="lazy" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-[#dbe4ea] bg-white">
        <div className="mx-auto max-w-7xl px-5 py-14 md:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0f5c3f]">
            {t("trustBar.title")}
          </p>
          <div className="mt-6 grid gap-6 md:grid-cols-4">
            {trust.map(([k, v]) => (
              <div key={k} className="flex items-start gap-3" data-testid={`trust-${k}`}>
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#0b3d5c] text-white">
                  <Check size={12} />
                </span>
                <p className="text-sm text-[#0b3d5c]">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#f2f6f9]">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <div className="max-w-2xl">
            <h2
              className="text-3xl leading-tight text-[#0b3d5c] md:text-4xl"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              data-testid="testimonials-title"
            >
              {t("testimonials.title")}
            </h2>
            <p className="mt-4 text-base text-[#4b5f70]">{t("testimonials.subtitle")}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {testimonials.map(([k, item]) => (
              <figure
                key={k}
                className="rounded-xl border border-[#dbe4ea] bg-white p-6"
                data-testid={`testimonial-${k}`}
              >
                <blockquote className="text-sm leading-relaxed text-[#0b3d5c]">"{item.quote}"</blockquote>
                <figcaption className="mt-4 border-t border-[#dbe4ea] pt-4">
                  <p className="text-sm font-semibold text-[#0b3d5c]">{item.name}</p>
                  <p className="text-xs text-[#4b5f70]">{item.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:grid-cols-2 md:gap-16 md:px-8">
          <div>
            <div className="overflow-hidden rounded-2xl border border-[#dbe4ea]">
              <img src={IMG.couple} alt={t("images.coupleAlt")} loading="lazy" className="h-full w-full object-cover" />
            </div>
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-[#dbe4ea] bg-[#f6f8fa] p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0b3d5c] text-white">
                <Landmark size={18} />
              </span>
              <p className="text-sm text-[#0b3d5c]">
                {t("footer.company")} · {t("footer.licenseLabel")} {t("footer.license")}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0f5c3f]">
              {t("newsletter.eyebrow")}
            </p>
            <h2
              className="mt-3 text-3xl leading-tight text-[#0b3d5c] md:text-4xl"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              data-testid="newsletter-title"
            >
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-base text-[#4b5f70]">{t("newsletter.subtitle")}</p>
            <div className="mt-8">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
